import { useState, useRef, useEffect } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { LANGUAGE } from "services/general";
import {
  pillButtonBase,
  pillButtonTone,
  pillBadgeClass,
} from "./navPillStyles";
import NotificationsPopupMenu from "./NotificationsPopupMenu";
import NotificationsMobileDrawer from "./NotificationsMobileDrawer";
import {
  useAgentNotifications,
  useNotificationsMutation,
  NOTIFICATIONS_READ_ALL_URL,
} from "modules/topbar/api";

const NOTIFICATIONS_CENTER_URL = `/${LANGUAGE}/notifications`;

const NotificationsMenu = ({ isDark, count = 0, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const { data, fetchLoading, refetch } = useAgentNotifications(open);
  const { create, loading } = useNotificationsMutation({
    onCreate: () => {
      refetch();
      onChanged && onChanged();
    },
  });

  const notifications = Array.isArray(data) ? data : [];

  // Close dropdown on click outside when on tablet/desktop (>= 640px) — the
  // phone drawer is closed via its own header button instead.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        window.innerWidth >= 640 &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  const closeMenu = () => setOpen(false);

  const markAllRead = () =>
    create({ url: NOTIFICATIONS_READ_ALL_URL, body: {} });

  // The notification is a link and navigates by itself; this only closes the menu.
  const handleOpenNotification = () => {
    closeMenu();
  };

  const handleViewAll = () => {
    closeMenu();
    navigate(NOTIFICATIONS_CENTER_URL);
  };

  const panelProps = {
    count,
    loading,
    markAllRead,
    notifications,
    fetchLoading,
    handleOpenNotification,
    handleViewAll,
  };

  return (
    <div className="relative" ref={menuRef}>
      <Tooltip title={t("settings.notifications")}>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`${pillButtonBase} ${pillButtonTone(isDark)}`}
        >
          <FontAwesomeIcon icon={faBell} className="text-[18px]" />
          {count > 0 && (
            <span className={pillBadgeClass(isDark)}>
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </Tooltip>

      {open && <NotificationsPopupMenu {...panelProps} />}

      {open && typeof document !== "undefined" && (
        <NotificationsMobileDrawer
          isDark={isDark}
          onClose={closeMenu}
          {...panelProps}
        />
      )}
    </div>
  );
};

export default NotificationsMenu;
